import { ActionIcon, Box, Group, Table, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import IconTrash from "@tabler/icons-react/dist/esm/icons/IconTrash.mjs";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import DateSearchFilter from "../../../../components/searchOptions/dateSearchFilter/DateSearchFilter";
import SortIcon from "../../../../components/searchOptions/SortIcon";
import ThrottledTextInput from "../../../../components/ThrottledTextInput";
import { DeleteConfirmModal } from "../../../../components/DeleteConfirmationModal";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { TableSkeleton } from "../../../../components/Skeletons/TableSkeleton";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";
import { flagStatuses } from "../../../../utils/vars";
import { useRole } from "../../../../redux/features/user";
import {
  clearReports,
  useSelectReportsData,
} from "../../../../redux/features/customReport/customReportsSlice";
import classes from "./customReportsTable.module.scss";
import useCustomReportsTable from "./useCustomReportsTable.logic";
import ActivateDeactivateButton from "./ActivateDeactivateButton/ActivateDeactivateButton";
import EditReportModal from "./EditReportPopup/EditReportModal";
import { dateFormat } from "../../../../utils/dateFormat";

export default function CustomReportsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const rowsData = useSelectReportsData();
  const [deleteId, setDeleteId] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const role = useRole();

  const COLUMNS = [
    { key: "date", label: t("rules.date"), sortKey: "DATE" },
    { key: "author", label: t("rules.author"), sortKey: "AUTHOR", width: 120 },
    {
      key: "ruleName",
      label: t("advancedSearchHistory.reportName"),
      sortKey: "RULE_NAME",
      width: 120,
    },
    { key: "status", label: t("rules.status"), sortKey: "STATUS", width: 90 },
    { key: "period", label: t("rules.period"), sortKey: "PERIOD", width: 90 },
    {
      key: "periodCount",
      label: t("rules.periodCount"),
      sortKey: "PERIOD_COUNT",
      width: 90,
    },
  ];

  const deleteReportPermission = role === "GRANT_ADMIN" || role === "ADMIN";
  const totalColumns = COLUMNS.length + 3 + (deleteReportPermission ? 1 : 0);

  const {
    take,
    page,
    totalCount,
    sort,
    filters,
    errorReportsFetch,
    isFetchingReports,
    deleteHandler,
    toggleSort,
    handleFilterChange,
    handlePageChange,
    handleTakeChange,
  } = useCustomReportsTable();

  useEffect(() => {
    if (errorReportsFetch) {
      toast.error("Error fetching custom reports", { toastId: 22 });
    }
  }, [errorReportsFetch]);

  useEffect(() => {
    return () => {
      dispatch(clearReports());
    };
  }, [dispatch]);

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    open();
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    close();
  };

  const onRowClick = (id) => {
    navigate(`/reports-custom/flags-advanced-search/${id}`);
  };

  const rows = rowsData.map((row, index) => (
    <Table.Tr onClick={() => onRowClick(row.id)} key={`${row.id}-${index}`}>
      <Table.Td>{dayjs(row.dateCreated).format(dateFormat)}</Table.Td>
      <Table.Td>{row?.author?.name}</Table.Td>
      <Tooltip label={row.name} disabled={!row.name}>
        <Table.Td>{row.name}</Table.Td>
      </Tooltip>
      <Table.Td>{flagStatuses[row.status] || row.status}</Table.Td>
      <Table.Td>{row.period}</Table.Td>
      <Table.Td>{row.periodCount}</Table.Td>
      <Table.Td onClick={(event) => event.stopPropagation()}>
        <ActivateDeactivateButton {...row} />
      </Table.Td>
      <Table.Td onClick={(event) => event.stopPropagation()}>
        <EditReportModal reportId={row.id} />
      </Table.Td>
      {deleteReportPermission ? (
        <Table.Td onClick={(event) => event.stopPropagation()}>
          <ActionIcon
            onClick={() => handleOpenDeleteModal(row.id)}
            c="gray"
            variant="transparent"
          >
            <IconTrash />
          </ActionIcon>
        </Table.Td>
      ) : null}
    </Table.Tr>
  ));

  return (
    <>
      <Box className="table-panel">
        <Table
          horizontalSpacing="md"
          highlightOnHover
          stickyHeader
          className={classes["custom-reports-table"]}
        >
          <Table.Thead>
            <Table.Tr>
              {COLUMNS.map((column) => (
                <Table.Th key={column.key}>
                  <Group>
                    <Group
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSort(column.sortKey)}
                      gap={4}
                      wrap="nowrap"
                    >
                      {column.label}
                      <SortIcon col={column.sortKey} sort={sort} />
                    </Group>
                    {column.key === "date" ? (
                      <DateSearchFilter
                        placeholder={t("rules.date")}
                        onChange={(dates) => handleFilterChange("DATE", dates)}
                      />
                    ) : (
                      <ThrottledTextInput
                        size="xs"
                        w={column.width}
                        delay={600}
                        value={filters[column.key]}
                        onThrottledChange={(value) =>
                          handleFilterChange(column.key, value)
                        }
                      />
                    )}
                  </Group>
                </Table.Th>
              ))}

              <Table.Th miw={150} />
              <Table.Th miw={60} />
              {deleteReportPermission ? <Table.Th miw={60} /> : null}
            </Table.Tr>
          </Table.Thead>

          {rowsData.length === 0 && !isFetchingReports ? (
            <Table.Tbody>
              <TableNoResults colSpan={totalColumns} />
            </Table.Tbody>
          ) : (
            <RenderTableWithSkeleton />
          )}
        </Table>

        <DeleteConfirmModal
          opened={opened}
          onClose={handleCloseDeleteModal}
          onDelete={() => deleteHandler(deleteId)}
          label="Are you sure that you want to delete this report?"
        />
      </Box>
      {totalCount > 0 && (
        <AppPagination
          page={page}
          take={take}
          total={totalCount}
          onPageChange={handlePageChange}
          onTakeChange={handleTakeChange}
        />
      )}
    </>
  );

  function RenderTableWithSkeleton() {
    if (isFetchingReports) {
      return <TableSkeleton cols={totalColumns} length={take} />;
    }

    return <Table.Tbody pos="relative">{rows}</Table.Tbody>;
  }
}
